import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface OnboardingShellProps {
  /** Layout variant. `default` = 1.25fr/1fr split. `wide` = 1.5fr/1fr (dense form col). `single` = no split (e.g. plan selection). */
  variant?: "default" | "wide" | "single";
  /** Form column content. */
  form: ReactNode;
  /** Brand column content. Omit for `variant="single"`. */
  brand?: ReactNode;
  className?: string;
}

/**
 * Outer onboarding shell. Cream paper stage, centered card with form/brand
 * split. Used by every onboarding screen except the auth modal (which has
 * its own dedicated frame with morphing states) and the plan selection
 * (which uses variant="single").
 *
 * At ≤980px the brand column is hidden — see onboarding.css.
 */
export function OnboardingShell({
  variant = "default",
  form,
  brand,
  className,
}: OnboardingShellProps): JSX.Element {
  const stageRef = useRef<HTMLDivElement>(null);

  // Reset scroll on shell mount so each new step's title is visible.
  // The scroll container is the parent layout (OnboardingLayout) — bubble up.
  useEffect(() => {
    let el: HTMLElement | null = stageRef.current;
    while (el && el !== document.body) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        el.scrollTop = 0;
        break;
      }
      el = el.parentElement;
    }
  }, []);

  return (
    <div ref={stageRef} className="fx-onb-stage">
      <div
        className={cn(
          "fx-onb-card",
          variant === "wide" && "wide",
          variant === "single" && "single",
          className,
        )}
      >
        <div className="fx-onb-form-col">{form}</div>
        {variant !== "single" && brand ? brand : null}
      </div>
    </div>
  );
}
